import { useState, useCallback } from 'react';
import { equipmentApi } from '../services/api';
import { Meta, EquipmentUnitFull } from '../types';
import { unwrapListResponse, unwrapMetaResponse } from '../apiUnwrapper/apiUnwrapper';

const DEFAULT_META: Meta = { total: 0, size: 5, page: 1 };

/**
 * Manages ONLY the equipment unit list shown in the main page table.
 * Types, definitions, and popup data are each owned by their respective components.
 */
export function useEquipmentData() {
  const [equipmentList, setEquipmentList] = useState<EquipmentUnitFull[]>([]);
  const [meta, setMeta] = useState<Meta>(DEFAULT_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEquipments = useCallback(async (page = 1, size = 5) => {
    try {
      setLoading(true);
      setError(null);
      const res = await equipmentApi.getEquipments(page, size);
      setEquipmentList(unwrapListResponse<EquipmentUnitFull>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      setError('Failed to load equipments.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchEquipments = useCallback(async (searchTerm: string, page = 1, size = 5) => {
    try {
      setLoading(true);
      setError(null);
      const res = await equipmentApi.getEquipmentsForSearchTerm(searchTerm, page, size);
      setEquipmentList(unwrapListResponse<EquipmentUnitFull>(res));
      setMeta(unwrapMetaResponse<Meta>(res) ?? DEFAULT_META);
    } catch (err) {
      setError('Failed to search equipments.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  return { equipmentList, meta, loading, error, setError, loadEquipments, searchEquipments };
}